import { HelpCircle } from 'lucide-react';
import { cn } from '@higgins/utils';
import { Button } from './ui/button';
import { HelpMarkdown } from './help-markdown';
import { SidePanel } from './side-panel';

export interface HelpPanelProps {
  /** Raw markdown to render as help content. Import the app's docs/USERS.md with `?raw`. */
  source: string;
  /** Heading shown at the top of the panel. Defaults to "Help & guidance". */
  title?: string;
  /** Visible text and accessible label for the trigger button. Defaults to "Help". */
  label?: string;
  /** Hide the text label and show only the help icon (for tight toolbars). */
  iconOnly?: boolean;
  /** Extra classes for the trigger button (e.g. to match a specific header). */
  triggerClassName?: string;
}

/**
 * Header help button that opens a right-hand slide-over rendering markdown help.
 *
 * Drop `<HelpPanel source={...} />` into the app's header/toolbar alongside the
 * other nav actions. The content is the single source of truth — pass the app's
 * `docs/USERS.md` imported with Vite's `?raw` suffix, so editing that file
 * updates the in-app help on the next build with no duplication.
 */
export function HelpPanel({
  source,
  title = 'Help & guidance',
  label = 'Help',
  iconOnly = false,
  triggerClassName,
}: HelpPanelProps) {
  return (
    <SidePanel
      title={title}
      description="In-app help and guidance for this application."
      closeLabel="Close help"
      trigger={
        <Button
          variant="ghost"
          size="sm"
          aria-label={label}
          className={cn('text-slate-600 hover:text-slate-900', triggerClassName)}
        >
          <HelpCircle className={cn('h-4 w-4', !iconOnly && 'mr-1.5')} />
          {!iconOnly && label}
        </Button>
      }
    >
      <HelpMarkdown source={source} />
    </SidePanel>
  );
}
